import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { BILLING_CONFIG, formatUsd } from "../lib/billingConfig";
import {
  cancelPremiumSubscription,
  subscribeManageableSubscriptions,
  type CancelSubscriptionResult,
  type ManageableSubscription,
} from "../lib/stripeCheckout";
import { track } from "../lib/telemetry";
import { Button } from "./ui/Button";

/**
 * Settings section for ending Premium at the current billing period.
 * Hidden unless the signed-in user has a real Stripe subscription
 * (`active`, `trialing`, or `past_due`). A local Premium override does
 * not surface this — there is nothing to cancel.
 *
 * Confirming calls `cancelPremiumSubscription({ action: "schedule" })`,
 * which sets `cancel_at_period_end`. The subscription stays active, and
 * Stripe includes this period's metered overage on the final invoice.
 */
export function CancelSubscription() {
  const auth = useAuth();
  const uid = auth.status === "signed-in" ? auth.user?.uid ?? null : null;
  const [subs, setSubs] = useState<ManageableSubscription[] | null>(null);
  const [pending, setPending] = useState<CancelSubscriptionResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setSubs(null);
      setPending(null);
      setConfirming(false);
      return;
    }
    setSubs(null);
    return subscribeManageableSubscriptions(uid, setSubs);
  }, [uid]);

  useEffect(() => {
    if (!pending || !subs) return;
    const liveScheduled =
      subs.length > 0 && subs.every((sub) => sub.cancelAtPeriodEnd);
    if (liveScheduled === pending.cancelAtPeriodEnd) setPending(null);
  }, [subs, pending]);

  if (!uid || !subs || subs.length === 0) return null;

  const cancelAtPeriodEnd = pending
    ? pending.cancelAtPeriodEnd
    : subs.every((sub) => sub.cancelAtPeriodEnd);
  const periodEnd = pending
    ? pending.currentPeriodEnd
    : latestPeriodEnd(subs);
  const when = formatPeriodEnd(periodEnd);
  const overage = formatUsd(BILLING_CONFIG.overagePriceUsdPerCredit);

  async function run(action: "schedule" | "resume") {
    setError(null);
    setBusy(true);
    try {
      const result = await cancelPremiumSubscription(action);
      setPending(result);
      setConfirming(false);
      track(
        action === "schedule"
          ? "premium_cancel_scheduled"
          : "premium_cancel_resumed"
      );
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Couldn't update your subscription."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="border-t border-ink-100 pt-4"
      data-testid="cancel-subscription"
    >
      <div className="text-xs font-medium text-ink-500 mb-2">Subscription</div>
      {cancelAtPeriodEnd ? (
        <div className="p-3 rounded-xl bg-ink-50 ring-1 ring-ink-100">
          <p
            className="text-body-sm text-ink-700 leading-snug"
            data-testid="cancel-subscription-ends"
          >
            {when ? (
              <>
                Your subscription ends on{" "}
                <span className="font-semibold text-ink-900">{when}</span>.
              </>
            ) : (
              <>Your subscription ends at the close of this billing period.</>
            )}{" "}
            You'll keep Premium until then, and extra credits used before
            that {when ? "date" : "time"} are billed on your final invoice
            at {overage} each.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            disabled={busy}
            onClick={() => void run("resume")}
            data-testid="cancel-subscription-resume"
          >
            {busy ? "Updating…" : "Keep subscription"}
          </Button>
        </div>
      ) : confirming ? (
        <div className="p-3 rounded-xl bg-amber-50 ring-1 ring-amber-200">
          <p className="text-body-sm text-amber-950 leading-snug">
            {when ? (
              <>
                Premium stays active until{" "}
                <span className="font-semibold">{when}</span>.
              </>
            ) : (
              <>Premium stays active until the end of this billing period.</>
            )}{" "}
            Extra credits you use before then are still charged on your
            final invoice at {overage} each. The subscription won't renew
            after that.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="danger"
              size="sm"
              disabled={busy}
              onClick={() => void run("schedule")}
              data-testid="cancel-subscription-confirm"
            >
              {busy ? "Canceling…" : "Confirm cancellation"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                setError(null);
              }}
              data-testid="cancel-subscription-dismiss"
            >
              Never mind
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
          data-testid="cancel-subscription-start"
        >
          Cancel subscription
        </Button>
      )}
      {error && (
        <div
          className="mt-2 p-2.5 rounded-lg bg-rose-50 ring-1 ring-rose-200 text-caption text-rose-800"
          data-testid="cancel-subscription-error"
        >
          {error}
        </div>
      )}
    </div>
  );
}

function latestPeriodEnd(subs: ManageableSubscription[]): number | null {
  let latest: number | null = null;
  for (const sub of subs) {
    if (sub.currentPeriodEnd == null) continue;
    if (latest == null || sub.currentPeriodEnd > latest) {
      latest = sub.currentPeriodEnd;
    }
  }
  return latest;
}

function formatPeriodEnd(seconds: number | null): string | null {
  if (seconds == null) return null;
  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
